/**
 * AttenuationsProcessor.js - Processeur pour la feuille "Atténuations (dB)"
 */

import { readExcelCell } from "./excelReader.js";
import { isEmptyOrNA, extractEventValues } from "./fiberCableUtilities.js";

export class AttenuationsProcessor {
  constructor(workbook) {
    this.workbook = workbook;
    this.sheetName = "Atténuations (dB)";
    this.worksheet = null;
    this.columns = null;
  }

  findSheet() {
    const errors = [];

    if (!this.workbook || !this.workbook.SheetNames) {
      errors.push("Workbook invalide");
      return { isValid: false, errors };
    }

    if (!this.workbook.SheetNames.includes(this.sheetName)) {
      errors.push(
        `Feuille "${this.sheetName}" non trouvée. Feuilles disponibles: ${this.workbook.SheetNames.join(", ")}`
      );
      return { isValid: false, errors };
    }

    this.worksheet = this.workbook.Sheets[this.sheetName];
    if (!this.worksheet) {
      errors.push(`Impossible d'accéder à la feuille "${this.sheetName}"`);
      return { isValid: false, errors };
    }

    return { isValid: true, errors: [] };
  }

  detectColumns() {
    const errors = [];

    if (!this.worksheet) {
      errors.push("Feuille non initialisée");
      return { isValid: false, errors };
    }

    try {
      this.columns = {
        ID_CABLE: {
          index: 1,
          letter: "B",
          name: "ID Câble",
        },
        N_FIBRE: {
          index: 2,
          letter: "C",
          name: "N° Fibre",
        },
        LAMBDA: {
          index: 4,
          letter: "E",
          name: "Lambda",
        },
      };

      // Trouver où commencent les événements numérotés (1, 2, 3, ...)
      const eventsStart = this.detectEventsStart();
      if (eventsStart === -1) {
        errors.push("Colonnes d'événements (1, 2, 3...) non trouvées");
        return { isValid: false, errors };
      }

      this.columns.EVENTS_START = eventsStart;
      this.columns.TOTAL_EVENT_COLUMNS = this.detectLastColumn();

      return { isValid: true, errors: [] };
    } catch (error) {
      errors.push(`Erreur de lecture des en-têtes: ${error.message}`);
      return { isValid: false, errors };
    }
  }

  detectEventsStart() {
    for (let col = 5; col < 50; col++) {
      const cellAddress = String.fromCharCode(65 + col) + "1";
      const cellValue = readExcelCell(this.worksheet[cellAddress]).trim();

      if (cellValue === "1") {
        return col;
      }
    }
    return -1;
  }

  detectLastColumn() {
    for (let col = this.columns.EVENTS_START; col < 100; col++) {
      const cellAddress = String.fromCharCode(65 + col) + "1";
      const cellValue = readExcelCell(this.worksheet[cellAddress]).trim();

      // Vérifier si c'est un numéro d'événement
      const eventNumber = parseInt(cellValue);
      if (isNaN(eventNumber) || eventNumber <= 0) {
        return col - this.columns.EVENTS_START; // Nombre de colonnes d'événements
      }
    }
    return 10; // Fallback
  }

  extractData() {
    const errors = [];

    if (!this.worksheet || !this.columns) {
      errors.push("Feuille ou colonnes non initialisées");
      return { isValid: false, errors, data: [] };
    }

    try {
      const data = [];
      const maxRows = 10000;

      for (let row = 5; row <= maxRows + 4; row++) {
        const idCableCell = this.worksheet[`${this.columns.ID_CABLE.letter}${row}`];
        const idCableValue = readExcelCell(idCableCell);

        // Si ID Câble est vide, la feuille est terminée
        if (this.isEndOfData(idCableValue)) {
          break;
        }

        const fiberCell = this.worksheet[`${this.columns.N_FIBRE.letter}${row}`];
        const lambdaCell = this.worksheet[`${this.columns.LAMBDA.letter}${row}`];

        const fiberValue = readExcelCell(fiberCell);
        const lambdaValue = readExcelCell(lambdaCell);

        // Extraire les événements
        const events = extractEventValues(
          this.worksheet,
          row,
          this.columns.EVENTS_START,
          this.columns.TOTAL_EVENT_COLUMNS
        );

        if (events.length === 0) {
          continue; // Pas d'événements pour cette ligne
        }

        data.push({
          EXCEL_ROW: row,
          ID_CABLE: idCableValue.trim(),
          N_FIBRE: parseInt(fiberValue) || 0,
          LAMBDA: parseFloat(lambdaValue) || 0,
          EVENTS: events,
        });
      }

      return { isValid: true, errors: [], data };
    } catch (error) {
      errors.push(`Erreur d'extraction des données: ${error.message}`);
      return { isValid: false, errors, data: [] };
    }
  }

  isEndOfData(idCableValue) {
    return !idCableValue || isEmptyOrNA(idCableValue);
  }
}
